import type { TableStatus, Reservation } from "@lemmonhq/types";

export interface TableWithAvailability {
  table: TableStatus;
  reservations: Reservation[];
  isAvailable: boolean;
  hasConflict: boolean;
}

/**
 * Computes availability for each table given the existing reservations
 * and the requested time + duration.
 *
 * Pure function — no Vue reactivity. Call from a computed() in the parent.
 */
export function computeTableAvailability(
  tables: TableStatus[],
  reservations: Reservation[],
  request: { time: string; duration_mins: number },
): TableWithAvailability[] {
  if (tables.length === 0) return [];

  const requestedStart = new Date(request.time);
  const requestedEnd = new Date(
    requestedStart.getTime() + request.duration_mins * 60000,
  );

  const result = tables.map((table) => {
    const tableReservations = reservations.filter((reservation) =>
      reservation.table_assignments?.some(
        (assignment) => assignment.table_id === table.id,
      ),
    );

    const hasConflict = tableReservations.some((reservation) => {
      const resStart = new Date(reservation.time);
      const resEnd = new Date(
        resStart.getTime() + reservation.duration_mins * 60000,
      );
      return requestedStart < resEnd && requestedEnd > resStart;
    });

    return {
      table,
      reservations: tableReservations,
      isAvailable: !hasConflict,
      hasConflict,
    };
  });

  // Sort: available tables first, then by name
  result.sort((a, b) => {
    if (a.isAvailable !== b.isAvailable) {
      return a.isAvailable ? -1 : 1;
    }
    return a.table.table.name.localeCompare(b.table.table.name);
  });

  return result;
}
